import React from 'react';
import {Platform, View} from 'react-native';
import {useStyles} from '../../../../shared/hooks/styles';
import {Text} from '../../../../shared/components/text';
import {useIsDesktop} from '../../../../shared/hooks/useIsDesktop';
import {useTheme} from '../../../../shared/theme';
import {DeliveryType} from '../../../../types/registerCardApplicationRequestPayload';
import {CardApplicationStatus} from '../../../../types/enums/cardApplicationStatus';
import {CardStatus} from '../../../../types/enums/cardStatus';
import {ExternalLink} from '../../../../shared/components/externalLink';
import {AuthorizeEsiaResponsePayload} from '../../../../shared/models/api/authorizeEsiaResponsePayload';
import {CounterpartyLinkedProfile} from '../../../../shared/hooks/useAccountLinkerHook/Types';
import {useIsSmallScreenDevice} from '../../../../shared/hooks/useIsSmallScreenDevice';

type CardApplicationStatusDescribed = Record<keyof typeof CardApplicationStatus, string> & {[key: string]: string};

type CardStatusDescribed = Record<keyof typeof CardStatus, string> & {[key: string]: string};

const cardApplicationsStatuses: CardApplicationStatusDescribed = {
  AcceptFromClient: 'Принято',
  OnReview: 'На рассмотрении',
  Accepted: 'Одобрено',
  Declined: 'Отказано',
};

const cardStatuses: CardStatusDescribed = {
  New: 'Выпущена',
  Active: 'Активная',
  Blocked: 'Заблокирована',
  Returned: 'Возвращена клиентом',
  InDelivery: 'В доставке',
  Arrived: 'Прибыло в место вручения',
  Reserved: 'Зарезервирована',
};

const russianPostTrackingUrl = 'https://www.pochta.ru/tracking?barcode=';

interface RussianPostDeliveryInfoProps {
  esiaRegInfo: CounterpartyLinkedProfile | AuthorizeEsiaResponsePayload | null | undefined;
  cardStatusFallback?: keyof typeof CardStatus;
  contentAlign?: 'center' | 'justify';
  canChangeDeliveryMethod?: boolean;
}

export function RussianPostDeliveryInfo({
  esiaRegInfo,
  cardStatusFallback,
  contentAlign = 'justify',
  canChangeDeliveryMethod = false,
}: RussianPostDeliveryInfoProps) {
  const theme = useTheme();
  const isDesktop = useIsDesktop();
  const isSmallScreen = useIsSmallScreenDevice();
  const isCentered = contentAlign === 'center';

  const styles = useStyles(() => {
    return {
      deliveryInfoContainer: {
        width: '100%',
        marginTop: isDesktop ? 0 : 24,
        paddingHorizontal: isCentered ? 14 : isSmallScreen ? 0 : 8,
        position: 'relative',
      },
      subtitle: {
        textAlign: contentAlign,
        marginTop: 10,
        marginBottom: 10,
        paddingHorizontal: isCentered ? 20 : 0,
        position: 'relative',
        ...(isCentered ? {width: '100%', alignSelf: 'stretch' as const} : null),
      },
      trackingNumberLink: {
        color: theme.color.linkColor,
      },
    };
  });

  if (esiaRegInfo?.cardDeliveryInfo?.deliveryTypeId !== DeliveryType.RussianPost) {
    return null;
  }

  const showCardStatus = esiaRegInfo?.cardStatusId != null && esiaRegInfo?.cardStatusId !== CardStatus.Blocked;

  let cardStatusLabel = null;
  if (cardStatusFallback != null) {
    cardStatusLabel = cardStatuses[CardStatus[esiaRegInfo.cardStatusId ?? cardStatusFallback]];
  } else if (esiaRegInfo.cardStatusId != null) {
    cardStatusLabel = cardStatuses[CardStatus[esiaRegInfo.cardStatusId]];
  }

  return (
    <View style={styles.deliveryInfoContainer}>
      {esiaRegInfo.cardApplicationStatusId != null && (
        <Text type='h6Light' style={styles.subtitle}>
          Статус заявления: {cardApplicationsStatuses[CardApplicationStatus[esiaRegInfo.cardApplicationStatusId]]}
        </Text>
      )}
      {showCardStatus && (
        <Text type='h6Light' style={styles.subtitle}>
          Статус карты: {cardStatusLabel}
        </Text>
      )}
      {isCentered ? (
        <Text type='h6Light' style={styles.subtitle}>
          Адрес получателя: {esiaRegInfo.cardDeliveryInfo.desiredAddress}
        </Text>
      ) : (
        <View style={styles.subtitle}>
          <Text type='h6Light'>Адрес получателя:</Text>
          <Text type='h6Light' style={{textAlign: 'justify'}}>
            {esiaRegInfo.cardDeliveryInfo.desiredAddress}
          </Text>
        </View>
      )}
      {esiaRegInfo.cardDeliveryInfo?.trackingNumber != null && (
        <Text type='h6Light' style={styles.subtitle}>
          Трек-номер (Почта России):{' '}
          {Platform.OS === 'web' ? (
            <a href={russianPostTrackingUrl + esiaRegInfo.cardDeliveryInfo?.trackingNumber} target='_blank'>
              {esiaRegInfo.cardDeliveryInfo?.trackingNumber}
            </a>
          ) : (
            <ExternalLink
              testID='trackingNumber'
              style={styles.trackingNumberLink}
              link={russianPostTrackingUrl + esiaRegInfo.cardDeliveryInfo?.trackingNumber}
              text={esiaRegInfo.cardDeliveryInfo?.trackingNumber}
            />
          )}
        </Text>
      )}
      {canChangeDeliveryMethod && (
        <Text type='h6Light' style={styles.subtitle}>
          Для данного заявления выбран способ получения через «Почту&nbsp;России».{'\n'}
          После оформления почтового отправления оператором вам станет доступна активация карты. Вы можете изменить
          способ получения и получить ЕСК в&nbsp;ближайшем пункте выдачи нефинансовых карт или&nbsp;в&nbsp;МФЦ.
        </Text>
      )}
    </View>
  );
}
